import {
  Eyebrow,
  Frame,
  Page,
  Sidebar,
  SideNav,
  Stack,
} from '@servicetitan/design-system';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import Navbar from './components/NavBar/NavBar';
import { permissionSets, RequirePermissions } from '@app/permissions';
import paths from '@app/routes/paths';
import classNames from 'classnames';
import styles from './MainLayout.module.css';
import PersonalSettingsIcon from '@assets/personal-settings.svg';
import Droppable from '@components/DragAndDrop/components/Droppable/Droppable';
import IncomingCallToastsGroup from '@modules/ToastModule/components/IncomingCallToast/IncomingCallToastGroup';
import { E911AddressBar } from './components/E911AddressBar/E911AddressBar';
import { userE911AddressState } from '@modules/E911AddressModule';
import { useSnapshot } from 'valtio';
import {
  FeatureFlagConfigurer,
  useFeatureFlags,
} from '@modules/FeatureFlagModule';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import HiddenFrame from './components/HiddenFrame/HiddenFrame';
import DebugPanel from './components/DebugPanel/DebugPanel';
import React from 'react';
import CallBarManager from './components/CallBarManager/CallBarManager';
import UrgentFollowUpsBadge from '@modules/UrgentFollowUpsModule/components/UrgentFollowUpsBadge';
import { mainLayoutLocators } from '@locators/main-layout';
import { useAbandonedCallsCountQuery } from '@modules/UrgentFollowUpsModule/AbandonedCallsModule';
import { useSecondChanceLeadsCountQuery } from '@modules/UrgentFollowUpsModule/SecondChanceLeadsModule';
import MainLoadingOverlay from './components/MainLoadingOverlay/MainLoadingOverlay';
import { QUEUE_MONITORING } from '../../../constants/labels';

const MainLayout = () => {
  const e911AddressState = useSnapshot(userE911AddressState);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [showDebugPanel, setShowDebugPanel] = React.useState(false);

  const isSettings = pathname.startsWith(`/settings`);
  const isActive = (path: string): boolean =>
    pathname === path || pathname.startsWith(`${path}/`);

  const featureFlags = useFeatureFlags();

  const handleToggleDebug = () => {
    setShowDebugPanel(!showDebugPanel);
  };

  const abandonedCallFollowUpsEnabled =
    featureFlags?.featureAbandonedCallFollowUps;
  const secondChanceLeadFollowUpsEnabled =
    featureFlags?.featureSecondChanceLeadFollowUps;

  const urgentFollowUpsEnabled =
    abandonedCallFollowUpsEnabled || secondChanceLeadFollowUpsEnabled;

  const manageAccountsEnabled = featureFlags?.featureManageAccounts;

  return (
    <Droppable>
      <MainLoadingOverlay>
        <Frame
          className={classNames('h-100', styles.mainPageFrame)}
          header={<Navbar onToggleDebug={handleToggleDebug} />}
        >
          {showDebugPanel && <DebugPanel />}
          <Page
            className={classNames('main-layout', styles.mainPage)}
            maxWidth='wide'
            spacing='none'
            sidebar={
              <Sidebar localStorageKey='layout.main.sidebar'>
                <SideNav
                  className={classNames({
                    [styles.sideNavSettings]: isSettings,
                  })}
                  title={isSettings ? 'Settings' : undefined}
                >
                  {!isSettings && (
                    <div className='m-t-2'>
                      <Stack
                        direction='column'
                        justifyContent='space-between'
                        className={styles.navSectionContainer}
                      >
                        <Stack.Item className={styles.navSectionMain}>
                          <Link to='/'>
                            <SideNav.Item
                              id={mainLayoutLocators.sidenav.inboxPageNavButton}
                              active={isActive('/')}
                            >
                              Inbox
                            </SideNav.Item>
                          </Link>
                          <RequirePermissions
                            permissions={
                              permissionSets.general.QUEUE_MONITORING
                            }
                            permissionsMatch='any'
                          >
                            <Link to='/queues'>
                              <SideNav.Item active={isActive('/queues')}>
                                {QUEUE_MONITORING}
                              </SideNav.Item>
                            </Link>
                          </RequirePermissions>
                          <Link to='/conversations'>
                            <SideNav.Item active={isActive('/conversations')}>
                              Conversation History
                            </SideNav.Item>
                          </Link>
                          {urgentFollowUpsEnabled && (
                            <SideNav.Item
                              active={isActive(
                                '/urgentfollowups/abandonedcalls',
                              )}
                              collapsible={{ expanded: true }}
                            >
                              <span className={styles.NoBoldExpanded}>
                                Urgent Follow Ups
                              </span>
                              <SideNav>
                                {abandonedCallFollowUpsEnabled && (
                                  <Link
                                    to='/urgentfollowups/abandonedcalls'
                                    // FIXME: `to` isn't being respected
                                    onClick={() =>
                                      navigate(
                                        '/urgentfollowups/abandonedcalls',
                                      )
                                    }
                                  >
                                    <SideNav.Item
                                      active={isActive(
                                        '/urgentfollowups/abandonedcalls',
                                      )}
                                    >
                                      Abandoned Calls
                                      <UrgentFollowUpsBadge
                                        id={
                                          mainLayoutLocators.sidenav
                                            .abandonedCallsBadge
                                        }
                                        badgeTitle='Number of new open abandoned calls'
                                        useCountQuery={
                                          useAbandonedCallsCountQuery
                                        }
                                      />
                                    </SideNav.Item>
                                  </Link>
                                )}
                                {secondChanceLeadFollowUpsEnabled && (
                                  <Link
                                    to='/urgentfollowups/secondchanceleads'
                                    onClick={() =>
                                      navigate(
                                        '/urgentfollowups/secondchanceleads',
                                      )
                                    }
                                  >
                                    <SideNav.Item
                                      active={isActive(
                                        '/urgentfollowups/secondchanceleads',
                                      )}
                                    >
                                      Second Chance Leads
                                      <UrgentFollowUpsBadge
                                        id={
                                          mainLayoutLocators.sidenav
                                            .secondChanceLeadsBadge
                                        }
                                        badgeTitle='Number of new open second chance leads'
                                        useCountQuery={
                                          useSecondChanceLeadsCountQuery
                                        }
                                      />
                                    </SideNav.Item>
                                  </Link>
                                )}
                              </SideNav>
                            </SideNav.Item>
                          )}
                        </Stack.Item>
                        {user?.personalLine?.phoneNumber && (
                          <Stack.Item>
                            <Link to='/personalsettings'>
                              <SideNav.Item
                                active={isActive('/personalsettings')}
                                className='p-b-1'
                              >
                                <Stack spacing={1}>
                                  <Stack.Item className='lh-1'>
                                    <PersonalSettingsIcon />
                                  </Stack.Item>

                                  <Stack.Item>My Phone Settings</Stack.Item>
                                </Stack>
                              </SideNav.Item>
                            </Link>
                            {e911AddressState.e911Address && (
                              <Stack direction='column' className='p-1'>
                                <Stack.Item>
                                  <Eyebrow>911 Address</Eyebrow>
                                </Stack.Item>
                                <Stack.Item className='m-l--half'>
                                  <E911AddressBar />
                                </Stack.Item>
                              </Stack>
                            )}
                          </Stack.Item>
                        )}
                      </Stack>
                    </div>
                  )}
                  {isSettings && (
                    <>
                      <RequirePermissions
                        permissions={permissionSets.admin.USER_MANAGEMENT}
                        permissionsMatch='any'
                      >
                        <Link to={paths.userManagement}>
                          <SideNav.Item
                            active={isActive(`/${paths.userManagement}`)}
                            id={
                              mainLayoutLocators.sidenav
                                .userManagementPageNavBtn
                            }
                          >
                            User Management
                          </SideNav.Item>
                        </Link>
                      </RequirePermissions>
                      <RequirePermissions
                        permissions={permissionSets.admin.TELECOM_WORKFLOWS}
                        permissionsMatch='any'
                      >
                        <Link to={paths.workflows}>
                          <SideNav.Item
                            active={isActive(`/${paths.workflows}`)}
                            id={
                              mainLayoutLocators.sidenav
                                .workflowManagementPageNavBtn
                            }
                          >
                            Routing Workflows
                          </SideNav.Item>
                        </Link>
                      </RequirePermissions>
                      {manageAccountsEnabled && (
                        <RequirePermissions
                          permissions={
                            permissionSets.admin.TELECOM_MANAGE_ACCOUNT
                          }
                          permissionsMatch='all'
                        >
                          <Link to={paths.manageAccounts}>
                            <SideNav.Item
                              active={isActive(`/${paths.manageAccounts}`)}
                            >
                              Manage Caller IDs
                            </SideNav.Item>
                          </Link>
                        </RequirePermissions>
                      )}
                      <RequirePermissions
                        permissions={permissionSets.admin.VIRTUAL_AGENT}
                        permissionsMatch='any'
                      >
                        <Link to={paths.virtualAgents}>
                          <SideNav.Item
                            active={isActive(`/${paths.virtualAgents}`)}
                          >
                            Virtual Agents
                          </SideNav.Item>
                        </Link>
                      </RequirePermissions>
                    </>
                  )}
                </SideNav>
              </Sidebar>
            }
          >
            <Outlet />
          </Page>
        </Frame>
        <CallBarManager />
        <IncomingCallToastsGroup />
        <FeatureFlagConfigurer />
        <HiddenFrame />
      </MainLoadingOverlay>
    </Droppable>
  );
};

export default MainLayout;
